/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/


// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";


import { useMemo, useRef, useState } from "react";
import moment from "moment";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from '@mui/material/Box';
import Divider from "@mui/material/Divider";
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import Grow from '@mui/material/Grow';
import InputAdornment from '@mui/material/InputAdornment';

import { TransitionGroup } from 'react-transition-group';


import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";

import PlaceholderCard from "examples/Cards/PlaceholderCard";


// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftDatePicker from "components/SoftDatePicker";
import Switch from "@mui/material/Switch";

// Soft UI Dashboard PRO React example components

import Calendar from "nextus/Calendars/Calendar";
import { Collapse, Tooltip } from "@mui/material";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const mapbox_settings = {
    style: 'mapbox://styles/harrymontano/cm01omrnz000u01omf2uegk93?optimize=true',
    zoom: 14,
    // maxZoom: 5
}

function getDaysInAdvance(display_time, start_time) {
    const display_time_moment = moment(display_time);
    const start_time_moment = moment(start_time);
    if (display_time_moment.isValid() && start_time_moment.isValid()) {
        var days_in_advance = start_time_moment.diff(display_time_moment, 'days');
        if (days_in_advance < 10 && days_in_advance > 0) {
            return days_in_advance;
        }
    }

    return 5;
}


function breakdownValues(input) {
    // console.log(input)
    const display_time = moment(input.display_time);
    const start_time = moment(input.start_time);
    const end_time = moment(input.end_time);
    return {
        is_multi_day: input.is_multi_day,
        warning_period_length: start_time.diff(display_time, 'days'),
        display_time: display_time.format("HH:mm:ss"),
        start_time: start_time.format("HH:mm:ss"),
        start_date: start_time.format("YYYY-MM-DD"),
        end_time: end_time.format("HH:mm:ss"),
        end_date: end_time.format("YYYY-MM-DD"),
    }

}

function combineDateAndTime(date, time) {
    return moment(date + ' ' + time);
}

function combineValues(input) {

    var ret = {};

    ret.start_time = combineDateAndTime(input.start_date, input.start_time);
    ret.end_time = combineDateAndTime(input.is_multi_day ? input.end_date : input.start_date, input.end_time);

    // console.log(ret)

    if (ret.end_time <= ret.start_time) {
        ret.end_time = moment(ret.end_time).add(1, 'days');
    }

    var time_arr = input.display_time.split(':');
    ret.display_time = ret.start_time.clone().subtract(input.warning_period_length, 'days').startOf('day').add(time_arr[0], 'hours').add(time_arr[1], 'minutes');

    // console.log(ret);

    return {
        start_time: ret.start_time.toDate(),
        end_time: ret.end_time.toDate(),
        display_time: ret.display_time.toDate(),
        is_multi_day: input.is_multi_day
    };
}


function CrowdCountingEventTiming({ timingData, setTimingData, isView }) {

    var groups_dict = {};
    const [timingSeparated, setTimingSeparated] = useState(breakdownValues(timingData));

    // console.log(timingSeparated);

    function updateValues(field, new_value) {
        // console.log(field, new_value)
        var new_timing = timingSeparated;
        new_timing[field] = new_value;

        setTimingData(combineValues(new_timing))
    }

    function applyValues(field) {
        return {
            "value": timingSeparated[field],
            "onChange": (new_value) => {
                updateValues(field, new_value.target.value);
            }
        }
    }

    return (<>

        <SoftBox my={2}>
            <SoftBox mb={0.5} ml={0.5} lineHeight={0} display="inline-block">
                <SoftTypography component="label" variant="button" fontWeight="bold">
                    Multiday Event
                </SoftTypography>
            </SoftBox>
            <SoftBox mt={0.25}>
                <Switch checked={timingSeparated.is_multi_day} onClick={() => { updateValues("is_multi_day", !timingSeparated.is_multi_day) }} />
            </SoftBox>
        </SoftBox>
        <SoftBox mb={2}>
            <SoftBox mb={0.5} ml={0.5} lineHeight={0} display="inline-block">
                <SoftTypography component="label" variant="button" fontWeight="bold">
                    {timingSeparated.is_multi_day ? "Event Start Date" : "Event Date"}
                </SoftTypography>
            </SoftBox>
            <SoftInput type="date" input={{ placeholder: "Select a date" }} {...applyValues('start_date')} />
        </SoftBox>
        <SoftBox mb={2}>
            <SoftBox mb={0.5} ml={0.5} lineHeight={0} display="inline-block">
                <SoftTypography component="label" variant="button" fontWeight="bold">
                    Event Start Time
                </SoftTypography>
            </SoftBox>
            <SoftInput type="time" step="900" input={{ placeholder: "Select a date" }} {...applyValues('start_time')} />
        </SoftBox>

        <TransitionGroup>
            {timingSeparated.is_multi_day && (
                <Collapse>
                    <SoftBox mb={2}>
                        <SoftBox mb={0.5} ml={0.5} lineHeight={0} display="inline-block">
                            <SoftTypography component="label" variant="button" fontWeight="bold">
                                Event End Date
                            </SoftTypography>
                        </SoftBox>
                        <SoftInput type="date" input={{ placeholder: "Select a date" }} {...applyValues('end_date')} />
                    </SoftBox>
                </Collapse>
            )}
        </TransitionGroup>

        <SoftBox mb={2}>
            <SoftBox mb={0.5} ml={0.5} lineHeight={0} display="inline-block">
                <SoftTypography component="label" variant="button" fontWeight="bold">
                    Event End Time
                </SoftTypography>
            </SoftBox>
            <SoftInput type="time" step="900" input={{ placeholder: "Select a date" }} {...applyValues('end_time')} />
        </SoftBox>


    </>);
}

// Typechecking props for the Calendar
CrowdCountingEventTiming.propTypes = {
    timingData: PropTypes.object,
    setTimingData: PropTypes.func,
    isView: PropTypes.bool,
};


export default CrowdCountingEventTiming;